import React, { useEffect, useState } from "react";
import { Box, Paper, Tooltip } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";
import { handleUnauthorized } from "../../TokenAuth/auth";
import { formatUTCDate, convertISTToUTCForAPI } from "../../utils/dateUtils";
import PageHeader1 from "../../components/common/PageHeader1";
import * as XLSX from "xlsx";
import {
    Button,
    TextField,
    CircularProgress,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
} from "@mui/material";

function PoojaRecommendationReport() {
    const [rowsData, setRowsData] = useState([]);
    const [loading, setLoading] = useState(false);

    const [pagination, setPagination] = useState({
        page: 0,
        pageSize: 10,
    });
    const [total, setTotal] = useState(0);

    const [searchQuery, setSearchQuery] = useState("");
    const [appliedSearchQuery, setAppliedSearchQuery] = useState("");
    const [searchType, setSearchType] = useState("astrologerName");
    const [appliedSearchType, setAppliedSearchType] = useState("astrologerName");

    const [startDate, setStartDate] = useState("");
    const [appliedStartDate, setAppliedStartDate] = useState("");

    const [endDate, setEndDate] = useState("");
    const [appliedEndDate, setAppliedEndDate] = useState("");

    const [statusFilter, setStatusFilter] = useState("");
    const [appliedStatusFilter, setAppliedStatusFilter] = useState("");

    const [isExporting, setIsExporting] = useState(false);

    const token = localStorage.getItem("astroVani-admin-token");
    const BASE_URL = process.env.REACT_APP_BASEURL;

    // ================= API =================
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const params = {
                    page: pagination.page + 1,
                    limit: pagination.pageSize,
                };

                if (appliedSearchQuery && appliedSearchQuery.trim()) {
                    if (appliedSearchType === "userMobile") {
                        params.userMobile = appliedSearchQuery;
                    } else if (appliedSearchType === "astrologerName") {
                        params.astrologerName = appliedSearchQuery;
                    } else if (appliedSearchType === "userName") {
                        params.userName = appliedSearchQuery;
                    } else if (appliedSearchType === "astrologerMobile") {
                        params.astrologerMobile = appliedSearchQuery;
                    } else if (appliedSearchType === "poojaName") {
                        params.poojaName = appliedSearchQuery;
                    } else if (appliedSearchType === "poojaBasePrice") {
                        params.poojaBasePrice = appliedSearchQuery;
                    }
                }

                if (appliedStartDate) {
                    params.startDate = convertISTToUTCForAPI(appliedStartDate, 'start');
                }
                if (appliedEndDate) {
                    params.endDate = convertISTToUTCForAPI(appliedEndDate, 'end');
                }
                if (appliedStatusFilter) {
                    params.status = appliedStatusFilter;
                }

                const res = await axios.get(
                    `${BASE_URL}/admin/pooja/recommendation/report`,
                    {
                        params,
                        headers: { Authorization: token },
                    }
                );

                setRowsData(res?.data?.data || []);
                // Ensure we read the total docs from the pagination object
                setTotal(res?.data?.paginationDetail?.totalDocs || 0);
            } catch (err) {
                if (err?.response?.status === 401) handleUnauthorized();
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [pagination, BASE_URL, token, appliedSearchQuery, appliedSearchType, appliedStartDate, appliedEndDate, appliedStatusFilter]);

    const handleApplyFilters = () => {
        setAppliedSearchQuery(searchQuery.trim());
        setAppliedSearchType(searchType);
        setAppliedStartDate(startDate);
        setAppliedEndDate(endDate);
        setAppliedStatusFilter(statusFilter);
        setPagination((prev) => ({ ...prev, page: 0 }));
    };

    const handleResetFilter = () => {
        setStartDate("");
        setEndDate("");
        setAppliedStartDate("");
        setAppliedEndDate("");
        setSearchQuery("");
        setAppliedSearchQuery("");
        setSearchType("astrologerName");
        setAppliedSearchType("astrologerName");
        setStatusFilter("");
        setAppliedStatusFilter("");
        setPagination((prev) => ({ ...prev, page: 0 }));
    };

    const exportToExcel = async () => {
        setIsExporting(true);
        try {
            const params = {
                page: 1,
                limit: 100000,
            };

            if (appliedSearchQuery && appliedSearchQuery.trim()) {
                if (appliedSearchType === "userMobile") {
                    params.userMobile = appliedSearchQuery;
                } else if (appliedSearchType === "astrologerName") {
                    params.astrologerName = appliedSearchQuery;
                } else if (appliedSearchType === "userName") {
                    params.userName = appliedSearchQuery;
                } else if (appliedSearchType === "astrologerMobile") {
                    params.astrologerMobile = appliedSearchQuery;
                } else if (appliedSearchType === "poojaName") {
                    params.poojaName = appliedSearchQuery;
                } else if (appliedSearchType === "poojaBasePrice") {
                    params.poojaBasePrice = appliedSearchQuery;
                }
            }
            if (appliedStartDate) {
                params.startDate = convertISTToUTCForAPI(appliedStartDate, 'start');
            }
            if (appliedEndDate) {
                params.endDate = convertISTToUTCForAPI(appliedEndDate, 'end');
            }
            if (appliedStatusFilter) {
                params.status = appliedStatusFilter;
            }

            const res = await axios.get(
                `${BASE_URL}/admin/pooja/recommendation/report`,
                {
                    params,
                    headers: { Authorization: token },
                }
            );

            const exportRows = res?.data?.data || [];

            if (exportRows.length === 0) {
                alert("No data available to export");
                setIsExporting(false);
                return;
            }

            const exportData = exportRows.map((row, index) => ({
                "S.No": index + 1,
                "Date": row.createdAt ? formatUTCDate(row.createdAt) : "N/A",
                "User Name": row.userName || "N/A",
                "User Mobile": row.userMobile || "N/A",
                "User Email": row.userEmail || "N/A",
                "Astrologer Name": row.astrologerName || "N/A",
                "Astrologer Mobile": row.astrologerMobile || "N/A",
                "Pooja Name": row.poojaName || "N/A",
                "Base Price": row.poojaBasePrice || 0,
                "Intake Name": row.intakeFormDetails?.name || "N/A",
                "Intake Gender": row.intakeFormDetails?.gender || "N/A",
                "Intake DOB": row.intakeFormDetails?.dob ? new Date(row.intakeFormDetails.dob).toLocaleDateString() : "N/A",
                "Urgency Level": row.urgencyLevel || "N/A",
                "Blessing Type": row.blessingType || "N/A",
                "Personalized Message": row.personalizedMessage || "-",
                "Status": row.status || "N/A",
            }));

            const worksheet = XLSX.utils.json_to_sheet(exportData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "PoojaRecommendationReport");
            XLSX.writeFile(workbook, `Pooja_Recommendation_Report_${new Date().toISOString().split('T')[0]}.xlsx`);
        } catch (error) {
            console.error("Export error:", error);
            alert("Failed to export data");
        } finally {
            setIsExporting(false);
        }
    };

    // ================= COLUMNS =================
    const columns = [
        {
            field: "createdAt",
            headerName: "Date",
            width: 130,
            renderCell: (params) => (
                <div>{params.row.createdAt ? formatUTCDate(params.row.createdAt) : "N/A"}</div>
            ),
        },
        {
            field: "userName",
            headerName: "User Details",
            width: 180,
            renderCell: (params) => (
                <div>
                    <div><strong>{params.row.userName || "N/A"}</strong></div>
                    <div style={{ fontSize: "0.8rem", color: "gray" }}>
                        {params.row.userMobile || "N/A"}
                    </div>
                    {params.row.userEmail && (
                        <div style={{ fontSize: "0.8rem", color: "gray" }}>
                            {params.row.userEmail}
                        </div>
                    )}
                </div>
            ),
        },
        {
            field: "astrologerName",
            headerName: "Astrologer",
            width: 180,
            renderCell: (params) => (
                <div>
                    <div><strong>{params.row.astrologerName || "N/A"}</strong></div>
                    <div style={{ fontSize: "0.8rem", color: "gray" }}>
                        {params.row.astrologerMobile || "N/A"}
                    </div>
                </div>
            ),
        },
        {
            field: "poojaName",
            headerName: "Pooja Name",
            width: 220,
            renderCell: (params) => (
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    {params.row.poojaImage ? (
                        <img
                            src={params.row.poojaImage}
                            alt={params.row.poojaName || "Pooja Image"}
                            style={{ width: 40, height: 40, borderRadius: "4px", objectFit: "cover" }}
                        />
                    ) : (
                        <div style={{ width: 40, height: 40, borderRadius: "4px", backgroundColor: "#e0e0e0" }} />
                    )}
                    <span>{params.row.poojaName || "N/A"}</span>
                </div>
            ),
        },
        {
            field: "poojaBasePrice",
            headerName: "Base Price",
            width: 120,
            renderCell: (params) => (
                <div>₹ {params.row.poojaBasePrice || 0}</div>
            ),
        },
        {
            field: "intakeFormDetails",
            headerName: "Intake Details",
            width: 200,
            renderCell: (params) => {
                const details = params.row.intakeFormDetails;
                if (!details) return <div>N/A</div>;
                return (
                    <div>
                        <div><strong>{details.name || "N/A"}</strong></div>
                        <div style={{ fontSize: "0.8rem", color: "gray", textTransform: "capitalize" }}>
                            Gender: {details.gender || "N/A"}
                        </div>
                        <div style={{ fontSize: "0.8rem", color: "gray" }}>
                            DOB: {details.dob ? new Date(details.dob).toLocaleDateString() : "N/A"}
                        </div>
                    </div>
                );
            }
        },
        {
            field: "urgencyLevel",
            headerName: "Urgency",
            width: 120,
            renderCell: (params) => (
                <div>{params.row.urgencyLevel || "N/A"}</div>
            ),
        },
        {
            field: "blessingType",
            headerName: "Blessing Type",
            width: 120,
            renderCell: (params) => (
                <div>{params.row.blessingType || "N/A"}</div>
            ),
        },
        {
            field: "personalizedMessage",
            headerName: "Message",
            flex: 1,
            minWidth: 200,
            renderCell: (params) => (
                <div>
                    {params.row.personalizedMessage ? (
                        <Tooltip title={params.row.personalizedMessage} arrow>
                            <span>
                                {params.row.personalizedMessage.length > 80
                                    ? `${params.row.personalizedMessage.slice(0, 80)}...`
                                    : params.row.personalizedMessage}
                            </span>
                        </Tooltip>
                    ) : (
                        "-"
                    )}
                </div>
            ),
        },
        {
            field: "status",
            headerName: "Status",
            width: 120,
            renderCell: (params) => {
                const isBooked = params.row.status === "Booked";
                return (
                    <span
                        style={{
                            color: isBooked ? "green" : "orange",
                            fontWeight: "bold",
                        }}
                    >
                        {params.row.status || "N/A"}
                    </span>
                );
            },
        },
    ];

    return (
        <div className="body d-flex">
            <div className="container-xxl">
                <PageHeader1 pagetitle="Pooja Recommendation Report" />

                {/* Search and Export Toolbar */}
                <Box sx={{ width: '100%', mb: 2 }}>
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={2}>
                        <FormControl size="small" sx={{ minWidth: 140, flexShrink: 0 }}>
                            <InputLabel>Search By</InputLabel>
                            <Select
                                value={searchType}
                                label="Search By"
                                onChange={(e) => setSearchType(e.target.value)}
                                sx={{
                                    borderRadius: '8px',
                                    '& .MuiOutlinedInput-input': {
                                        padding: '6px 14px',
                                    },
                                    '& .MuiInputBase-root': {
                                        height: '32px',
                                    }
                                }}
                            >
                                <MenuItem value="userName">User Name</MenuItem>
                                <MenuItem value="userMobile">User Mobile</MenuItem>
                                <MenuItem value="astrologerName">Astrologer Name</MenuItem>
                                <MenuItem value="astrologerMobile">Astrologer Mobile</MenuItem>
                                <MenuItem value="poojaName">Pooja Name</MenuItem>
                                <MenuItem value="poojaBasePrice">Base Price</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            placeholder={
                                searchType === "userName" ? "Search by user name..." :
                                    searchType === "userMobile" ? "Search by user mobile..." :
                                        searchType === "astrologerName" ? "Search by astrologer name..." :
                                            searchType === "astrologerMobile" ? "Search by astrologer mobile..." :
                                                searchType === "poojaName" ? "Search by pooja name..." :
                                                    "Search by base price..."
                            }
                            variant="outlined"
                            size="small"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyPress={(e) => { if (e.key === 'Enter') { handleApplyFilters(); } }}
                            sx={{
                                width: { xs: '150px', sm: '180px', md: '210px' },
                                flexShrink: 0,
                                background: 'var(--card-color)',
                                borderRadius: '9px',
                                '& .MuiInputBase-input': {
                                    fontSize: '13px',
                                    padding: '6px 10px 6px 0',
                                    height: '20px'
                                },
                                '& .MuiInputBase-root': {
                                    height: '32px',
                                }
                            }}
                        />
                        <TextField
                            label="Start Date"
                            type="date"
                            variant="outlined"
                            size="small"
                            value={startDate || ""}
                            onChange={(e) => setStartDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{
                                width: { xs: '140px', sm: '160px', md: '180px' },
                                flexShrink: 0,
                                '& .MuiInputBase-input[type="date"]': {
                                    paddingRight: '8px',
                                    paddingLeft: '14px',
                                    paddingTop: '6px',
                                    paddingBottom: '6px',
                                    height: '20px',
                                },
                                '& .MuiOutlinedInput-root': {
                                    paddingRight: '8px',
                                    height: '32px',
                                },
                            }}
                        />
                        <TextField
                            label="End Date"
                            type="date"
                            variant="outlined"
                            size="small"
                            value={endDate || ""}
                            onChange={(e) => setEndDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{
                                width: { xs: '140px', sm: '160px', md: '180px' },
                                flexShrink: 0,
                                '& .MuiInputBase-input[type="date"]': {
                                    paddingRight: '8px',
                                    paddingLeft: '14px',
                                    paddingTop: '6px',
                                    paddingBottom: '6px',
                                    height: '20px',
                                },
                                '& .MuiOutlinedInput-root': {
                                    paddingRight: '8px',
                                    height: '32px',
                                },
                            }}
                        />
                        <FormControl size="small" sx={{ minWidth: 140, flexShrink: 0 }}>
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={statusFilter}
                                label="Status"
                                onChange={(e) => setStatusFilter(e.target.value)}
                                sx={{
                                    borderRadius: '8px',
                                    '& .MuiOutlinedInput-input': {
                                        padding: '6px 14px',
                                    },
                                    '& .MuiInputBase-root': {
                                        height: '32px',
                                    }
                                }}
                            >
                                <MenuItem value="">All Status</MenuItem>
                                <MenuItem value="Booked">Booked</MenuItem>
                                <MenuItem value="Suggested">Suggested</MenuItem>
                                {/* <MenuItem value="Completed">Completed</MenuItem>
                                <MenuItem value="Cancelled">Cancelled</MenuItem> */}
                            </Select>
                        </FormControl>
                        <Button
                            variant="contained"
                            onClick={handleApplyFilters}
                            disabled={loading || (!searchQuery.trim() && !startDate && !endDate && !statusFilter)}
                            sx={{
                                borderRadius: "8px",
                                textTransform: "none",
                                backgroundColor: "#E7B242",
                                color: "#fff",
                                height: 32,
                                px: 2,
                                flexShrink: 0,
                                fontSize: '13px',
                                "&:hover": { backgroundColor: "#E7B242", opacity: 0.9 },
                                "&:disabled": {
                                    backgroundColor: "rgba(0, 0, 0, 0.12)",
                                    color: "rgba(0, 0, 0, 0.26)",
                                },
                            }}
                        >
                            Apply Filters
                        </Button>
                    </Box>
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                        <Button
                            variant="outlined"
                            onClick={handleResetFilter}
                            sx={{
                                borderRadius: '8px',
                                textTransform: 'none',
                                borderColor: '#E7B242',
                                color: '#E7B242',
                                height: 32,
                                px: 2,
                                fontSize: '13px',
                                '&:hover': { borderColor: '#E7B242', backgroundColor: 'rgba(231, 178, 66, 0.08)' },
                                flexShrink: 0
                            }}
                        >
                            Reset
                        </Button>
                        <Button
                            variant="contained"
                            onClick={exportToExcel}
                            disabled={isExporting}
                            sx={{
                                borderRadius: "8px",
                                textTransform: "none",
                                backgroundColor: "#E7B242",
                                color: "#fff",
                                height: 32,
                                px: 2,
                                fontSize: '13px',
                                "&:hover": { backgroundColor: "#E7B242", opacity: 0.9 },
                                "&:disabled": { backgroundColor: "#E7B242", opacity: 0.6 },
                            }}
                        >
                            {isExporting ? (
                                <>
                                    <CircularProgress size={16} sx={{ color: "#fff", mr: 1 }} />
                                    Exporting...
                                </>
                            ) : (
                                "Export to Excel"
                            )}
                        </Button>
                    </Box>
                </Box>

                <Paper sx={{ p: 2 }}>
                    <Box sx={{ minHeight: 400, width: "100%" }}>
                        <DataGrid
                            rows={rowsData}
                            columns={columns}
                            getRowId={(row) => row._id}
                            loading={loading}
                            paginationMode="server"
                            rowCount={total}
                            pageSizeOptions={[10, 20, 50]}
                            paginationModel={pagination}
                            onPaginationModelChange={(newModel) => setPagination(newModel)}
                            disableRowSelectionOnClick
                            getRowHeight={() => 'auto'}
                            sx={{
                                '& .MuiDataGrid-cell': {
                                    paddingTop: '10px',
                                    paddingBottom: '10px',
                                }
                            }}
                        />
                    </Box>
                </Paper>
            </div>
        </div>
    );
}

export default PoojaRecommendationReport;
